import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, Cell } from "recharts";
import { DollarSign, TrendingUp, Zap, Trophy } from "lucide-react";

const money = (n) => `$${Math.round(n).toLocaleString()}`;

function Stat({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <Card className="bg-slate-900 border-slate-800 p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-2">{label}</p>
          <p className="font-display text-3xl font-extrabold text-white font-mono tracking-tight">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

const FUNNEL_COLORS = ["#38BDF8", "#F97316", "#F59E0B", "#10B981", "#059669"];

export default function Analytics() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/analytics/overview").then(r => setStats(r.data)); }, []);

  if (!stats) return <div className="p-10 text-slate-500">Loading analytics…</div>;

  return (
    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto">
      <header className="mb-6">
        <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-2">Analytics</p>
        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">Recovery & ROI</h1>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat testId="an-recovered" icon={DollarSign} accent="bg-emerald-500/15 text-emerald-400" label="Recovered Revenue" value={money(stats.recovered_revenue)} sub={`${stats.booked_estimates} booked`} />
        <Stat testId="an-won" icon={Trophy} accent="bg-emerald-600/20 text-emerald-300" label="Won Revenue" value={money(stats.won_revenue)} sub={`${stats.won_deals} deals`} />
        <Stat testId="an-conv" icon={TrendingUp} accent="bg-amber-500/15 text-amber-400" label="Conversion" value={`${stats.conversion_rate}%`} sub="Missed → Booked" />
        <Stat testId="an-response" icon={Zap} accent="bg-orange-500/15 text-orange-400" label="Avg Response" value={`${stats.avg_response_seconds}s`} sub="AI SMS reply" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 bg-slate-900 border-slate-800 p-5">
          <h3 className="font-display text-lg font-semibold mb-1">Recovered revenue — last 7 days</h3>
          <p className="text-xs text-slate-500 mb-4 font-mono">Booked & won revenue by day</p>
          <div className="h-72">
            <ResponsiveContainer>
              <AreaChart data={stats.trend}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F97316" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#F97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${v/1000}k`} />
                <Tooltip
                  contentStyle={{ background: "#0B0F17", border: "1px solid #374151", borderRadius: 8, color: "#F8FAFC" }}
                  formatter={(v) => [`$${v.toLocaleString()}`, "Revenue"]}
                />
                <Area type="monotone" dataKey="revenue" stroke="#F97316" strokeWidth={2} fill="url(#grad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="bg-slate-900 border-slate-800 p-5">
          <h3 className="font-display text-lg font-semibold mb-1">Conversion funnel</h3>
          <p className="text-xs text-slate-500 mb-4 font-mono">Missed → Won stages</p>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={stats.funnel} layout="vertical">
                <XAxis type="number" stroke="#64748B" fontSize={12} />
                <YAxis type="category" dataKey="stage" stroke="#64748B" fontSize={12} width={80} />
                <Tooltip
                  contentStyle={{ background: "#0B0F17", border: "1px solid #374151", borderRadius: 8, color: "#F8FAFC" }}
                />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {stats.funnel.map((_, i) => <Cell key={i} fill={FUNNEL_COLORS[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="mt-4 bg-slate-900 border-slate-800 p-5">
        <h3 className="font-display text-lg font-semibold mb-1">Leads captured — last 7 days</h3>
        <p className="text-xs text-slate-500 mb-4 font-mono">Missed calls detected and auto-replied by AI</p>
        <div className="h-56">
          <ResponsiveContainer>
            <BarChart data={stats.trend}>
              <XAxis dataKey="day" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip
                contentStyle={{ background: "#0B0F17", border: "1px solid #374151", borderRadius: 8, color: "#F8FAFC" }}
              />
              <Bar dataKey="captured" fill="#10B981" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
